import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import ProductCard from './ProductCard';
import StoreCard from './StoreCard';
import AuctionCard from './AuctionCard';
import Pagination from './Pagination';
import FilterPanel from './FilterPanel';
import CampaignBannerCarousel from '../Campaign/CampaignBannerCarousel';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useReview } from '../../context/ReviewContext';
import { useAuth } from '../../context/AuthContext';
import { setDocumentTitle } from '../../utils/documentTitle';
import BottomNavigation from '../Layout/BottomNavigation';

const MarketplaceHome = () => {
  const { t } = useTranslation();
  const { addToCart, createAuction, placeBid } = useMarketplace();
  const { getProductReviewStats, getStoreReviewStats } = useReview();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStoreId, setUserStoreId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minRating: '',
    storeLevel: '',
    sortBy: 'newest',
    type: 'all' // all, sell, buy, auction
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [auctionCurrentPage, setAuctionCurrentPage] = useState(1);
  const [auctionItemsPerPage] = useState(6); // Show 6 auctions per page on homepage

  const categories = [
    { id: 'all', name: t('marketplace.all') },
    { id: 'Flowers', name: t('marketplace.flowers') },
    { id: 'Seeds', name: t('marketplace.seeds') },
    { id: 'Edibles', name: t('marketplace.edibles') },
    { id: 'Pre Rolls', name: t('marketplace.preRolls') },
    { id: 'Cannabis Oils', name: t('marketplace.cannabisOils') },
    { id: 'CBD Products', name: t('marketplace.cbdProducts') }
  ];

  // Read category from URL and update when URL changes
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    } else {
      // ถ้าไม่มี category parameter ให้ reset เป็น 'all'
      setSelectedCategory('all');
    }
  }, [searchParams]);

  useEffect(() => {
    setDocumentTitle(t('marketplace.title'));
    fetchData();
  }, [selectedCategory, searchTerm, currentUser, t]);

  // Apply filters and pagination
  useEffect(() => {
    applyFilters();
  }, [products, filters]);

  const applyFilters = async () => {
    let filtered = [...products];

    // Price filter
    if (filters.minPrice) {
      filtered = filtered.filter(p => p.price >= filters.minPrice);
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(p => p.price <= filters.maxPrice);
    }

    // Rating filter (ต้องดึง review stats)
    if (filters.minRating) {
      const productsWithRatings = await Promise.all(
        filtered.map(async (product) => {
          try {
            const stats = await getProductReviewStats(product.id);
            return { ...product, rating: stats.averageRating };
          } catch (error) {
            return { ...product, rating: 0 };
          }
        })
      );
      filtered = productsWithRatings.filter(p => p.rating >= filters.minRating);
    }

    // Store level filter (ต้องดึง store info)
    if (filters.storeLevel) {
      const productsWithStoreLevel = await Promise.all(
        filtered.map(async (product) => {
          if (product.storeId) {
            try {
              const storeRef = doc(db, 'stores', product.storeId);
              const storeSnap = await getDoc(storeRef);
              if (storeSnap.exists()) {
                const storeData = storeSnap.data();
                return { ...product, storeLevel: storeData.reputationLevel };
              }
            } catch (error) {
              console.error('Error fetching store:', error);
            }
          }
          return product;
        })
      );
      filtered = productsWithStoreLevel.filter(p => p.storeLevel === filters.storeLevel);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'priceLow':
          return (a.price || 0) - (b.price || 0);
        case 'priceHigh':
          return (b.price || 0) - (a.price || 0);
        case 'oldest':
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return aTime - bTime;
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'popular':
          return (b.viewCount || 0) - (a.viewCount || 0);
        default: // newest
          const aTimeNew = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTimeNew = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTimeNew - aTimeNew;
      }
    });

    setFilteredProducts(filtered);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // ดึง userStoreId ถ้ามี currentUser
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setUserStoreId(userData.storeId || null);
          }
        } catch (error) {
          console.warn('Error fetching user storeId:', error);
          setUserStoreId(null);
        }
      } else {
        setUserStoreId(null);
      }

      // ดึงสินค้า - ใช้ fallback query ถ้าไม่มี index
      let productsData = [];
      try {
        let productsQuery = query(
          collection(db, 'products'),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(12)
        );

        if (selectedCategory !== 'all') {
          productsQuery = query(
            collection(db, 'products'),
            where('status', '==', 'active'),
            where('category', '==', selectedCategory),
            orderBy('createdAt', 'desc'),
            limit(12)
          );
        }

        const productsSnapshot = await getDocs(productsQuery);
        productsData = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (indexError) {
        // Fallback: query โดยไม่มี orderBy
        console.warn('Index not found, using fallback query:', indexError);
        let fallbackQuery = query(
          collection(db, 'products'),
          where('status', '==', 'active'),
          limit(50)
        );

        if (selectedCategory !== 'all') {
          fallbackQuery = query(
            collection(db, 'products'),
            where('status', '==', 'active'),
            where('category', '==', selectedCategory),
            limit(50)
          );
        }

        const fallbackSnapshot = await getDocs(fallbackQuery);
        productsData = fallbackSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually
        productsData.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        productsData = productsData.slice(0, 12);
      }

      // กรองด้วย searchTerm
      const filteredProducts = searchTerm
        ? productsData.filter(p => 
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.description?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : productsData;

      setProducts(filteredProducts);

      // ดึงร้านค้า - ใช้ fallback query ถ้าไม่มี index
      // ดึงร้านที่ status เป็น 'active' หรือ 'pending' (ร้านที่เพิ่งเปิด)
      let storesData = [];
      try {
        // Query ร้านที่ active
        const activeStoresQuery = query(
          collection(db, 'stores'),
          where('status', '==', 'active'),
          orderBy('reputationScore', 'desc'),
          limit(8)
        );
        const activeStoresSnapshot = await getDocs(activeStoresQuery);
        storesData = activeStoresSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (indexError) {
        console.warn('Index not found for stores, using fallback query:', indexError);
        // Fallback: ดึงร้านทั้งหมดแล้ว filter
        const fallbackQuery = query(
          collection(db, 'stores'),
          limit(100)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        storesData = fallbackSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(store => store.status === 'active' || store.status === 'pending');
        
        // Sort manually
        storesData.sort((a, b) => (b.reputationScore || 0) - (a.reputationScore || 0));
        storesData = storesData.slice(0, 8);
      }
      setStores(storesData);

      // ดึงประมูล - ใช้ fallback query ถ้าไม่มี index
      let auctionsData = [];
      try {
        const auctionsQuery = query(
          collection(db, 'auctions'),
          where('status', '==', 'active'),
          orderBy('endTime', 'asc'),
          limit(6)
        );
        const auctionsSnapshot = await getDocs(auctionsQuery);
        auctionsData = auctionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (indexError) {
        console.warn('Index not found for auctions, using fallback query:', indexError);
        const fallbackQuery = query(
          collection(db, 'auctions'),
          where('status', '==', 'active'),
          limit(50)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        auctionsData = fallbackSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually
        auctionsData.sort((a, b) => {
          const aTime = a.endTime?.toDate?.() || new Date(a.endTime || 0);
          const bTime = b.endTime?.toDate?.() || new Date(b.endTime || 0);
          return aTime - bTime;
        });
        auctionsData = auctionsData.slice(0, 6);
      }

      // ดึงรูปภาพจาก product ถ้า auction มี productId
      const auctionsWithImages = await Promise.all(
        auctionsData.map(async (auction) => {
          if (auction.productId && !auction.productImage && !auction.productImages?.[0] && !auction.images?.[0]) {
            try {
              const productRef = doc(db, 'products', auction.productId);
              const productSnap = await getDoc(productRef);
              if (productSnap.exists()) {
                const productData = productSnap.data();
                return {
                  ...auction,
                  productImage: productData.images?.[0] || null,
                  productImages: productData.images || []
                };
              }
            } catch (error) {
              console.error('Error fetching product image for auction:', error);
            }
          }
          return auction;
        })
      );

      setAuctions(auctionsWithImages);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleAddToCart = async (product) => {
    try {
      await addToCart(product);
      alert('เพิ่มสินค้าลงตะกร้าแล้ว!');
    } catch (error) {
      alert(error.message || 'เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Search Bar with Filter Icon - Sticky below Navbar */}
      <div className="bg-white border-b border-gray-200 sticky top-[56px] md:top-[64px] z-30">
        <div className="container mx-auto px-3 py-2.5">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                🔍
              </span>
              <input
                type="text"
                placeholder={t('marketplace.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <button
              onClick={() => setShowFilterModal(true)}
              className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center"
            >
              <span className="text-sm">🔽</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">{t('marketplace.filter')}</h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.type')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {['all', 'sell', 'buy', 'auction'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setFilters({ ...filters, type })}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        filters.type === type
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {type === 'all' ? t('marketplace.all') : type === 'sell' ? t('marketplace.sell') : type === 'buy' ? t('marketplace.buy') : t('auction.title')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setFilters({ ...filters, type: 'all' });
                  setShowFilterModal(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                รีเซ็ต
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                ตกลง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Banner Carousel - Mobile Optimized */}
      <CampaignBannerCarousel
        positionId="homepage-top"
        userStoreId={userStoreId}
      />

      <div className="container mx-auto px-3 py-3 md:px-4 md:py-8">
        {/* Filter Panel - Hidden on mobile */}
        <div className="hidden md:block mb-4">
          <FilterPanel
            filters={filters}
            onFilterChange={setFilters}
            onReset={() => {
              setFilters({
                minPrice: '',
                maxPrice: '',
                minRating: '',
                storeLevel: '',
                sortBy: 'newest'
              });
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Category Icons - Mobile Optimized */}
        <div className="mb-4 md:mb-8">
          <div className="flex gap-3 md:gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(category => {
              const categoryIcons = {
                'all': '🌿',
                'Flowers': '🌸',
                'Seeds': '🌱',
                'Edibles': '🍪',
                'Pre Rolls': '🚬',
                'Cannabis Oils': '💧',
                'CBD Products': '💊'
              };
              const icon = categoryIcons[category.id] || '🌿';
              
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex flex-col items-center justify-center flex-shrink-0 w-16 md:w-auto md:px-4 md:py-2 transition-colors ${
                    selectedCategory === category.id
                      ? 'text-green-600'
                      : 'text-gray-600'
                  }`}
                >
                  <div className={`w-12 h-12 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xl md:text-base mb-1 md:mb-0 ${
                    selectedCategory === category.id
                      ? 'bg-green-100'
                      : 'bg-gray-100'
                  }`}>
                    {icon}
                  </div>
                  <span className={`text-xs md:text-sm font-medium whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'font-semibold'
                      : ''
                  }`}>
                    {category.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ประมูลกำลังดำเนินการ */}
        {auctions.length > 0 && (
          <section className="mb-4 md:mb-12">
            <div className="flex items-center justify-between gap-2 md:gap-3 mb-4 flex-wrap">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">🔨 {t('marketplace.auctionsInProgress')}</h2>
              <div className="flex items-center gap-2 md:gap-3">
                {currentUser && (
                  <Link
                    to="/marketplace/my-auctions"
                    className="text-yellow-600 hover:text-yellow-700 font-semibold text-sm md:text-base flex items-center gap-1"
                  >
                    <span>👤</span>
                    <span>ประมูลของฉัน</span>
                  </Link>
                )}
                <Link
                  to="/marketplace/auctions"
                  className="text-green-600 hover:text-green-700 font-semibold text-sm md:text-base flex items-center gap-1"
                >
                  <span>ดูทั้งหมด</span>
                  <span>→</span>
                </Link>
              </div>
            </div>
            
            {/* Auctions Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3">
              {(() => {
                const auctionTotalPages = Math.ceil(auctions.length / auctionItemsPerPage);
                const auctionStartIndex = (auctionCurrentPage - 1) * auctionItemsPerPage;
                const auctionEndIndex = auctionStartIndex + auctionItemsPerPage;
                const paginatedAuctions = auctions.slice(auctionStartIndex, auctionEndIndex);
                
                return paginatedAuctions.map(auction => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    onPlaceBid={placeBid}
                  />
                ));
              })()}
            </div>

            {/* Auction Pagination */}
            {(() => {
              const auctionTotalPages = Math.ceil(auctions.length / auctionItemsPerPage);
              
              if (auctionTotalPages > 1) {
                return (
                  <div className="mt-4 mb-20 md:mb-4 flex items-center justify-center gap-2">
                    {/* Mobile: Simple Previous/Next */}
                    <div className="md:hidden flex items-center gap-2">
                      <button
                        onClick={() => setAuctionCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={auctionCurrentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ‹‹ ก่อนหน้า
                      </button>
                      <span className="text-sm text-gray-600 px-2">
                        {auctionCurrentPage} / {auctionTotalPages}
                      </span>
                      <button
                        onClick={() => setAuctionCurrentPage(prev => Math.min(auctionTotalPages, prev + 1))}
                        disabled={auctionCurrentPage === auctionTotalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ถัดไป ››
                      </button>
                    </div>

                    {/* Desktop: Full Pagination */}
                    <div className="hidden md:flex items-center gap-2">
                      <button
                        onClick={() => setAuctionCurrentPage(1)}
                        disabled={auctionCurrentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="หน้าแรก"
                      >
                        ««
                      </button>
                      
                      <button
                        onClick={() => setAuctionCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={auctionCurrentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="หน้าก่อนหน้า"
                      >
                        ‹
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, auctionTotalPages) }, (_, i) => {
                          let pageNum;
                          if (auctionTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (auctionCurrentPage <= 3) {
                            pageNum = i + 1;
                          } else if (auctionCurrentPage >= auctionTotalPages - 2) {
                            pageNum = auctionTotalPages - 4 + i;
                          } else {
                            pageNum = auctionCurrentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setAuctionCurrentPage(pageNum)}
                              className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                                auctionCurrentPage === pageNum
                                  ? 'bg-yellow-500 text-white border-yellow-500'
                                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setAuctionCurrentPage(prev => Math.min(auctionTotalPages, prev + 1))}
                        disabled={auctionCurrentPage === auctionTotalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="หน้าถัดไป"
                      >
                        ›
                      </button>
                      
                      <button
                        onClick={() => setAuctionCurrentPage(auctionTotalPages)}
                        disabled={auctionCurrentPage === auctionTotalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="หน้าสุดท้าย"
                      >
                        »»
                      </button>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </section>
        )}

        {/* สินค้าแนะนำ - Mobile Optimized */}
        <section className="mb-4 md:mb-12">
          <div className="flex justify-between items-center mb-3 md:mb-4">
            <h2 className="text-lg md:text-2xl font-bold text-gray-800">{t('marketplace.recommendedProducts')}</h2>
            <Link
              to="/marketplace/products"
              className="text-green-600 hover:text-green-700 font-semibold text-sm md:text-base"
            >
              {t('marketplace.viewAll')} →
            </Link>
          </div>
          {filteredProducts.length > 0 ? (
            <>
              {/* Calculate pagination */}
              {(() => {
                const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

                return (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3">
                      {paginatedProducts.map(product => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          onAddToCart={handleAddToCart}
                          userStoreId={userStoreId}
                        />
                      ))}
                    </div>
                    {/* Pagination - Mobile: Simple, Desktop: Full */}
                    <div className="mt-4 mb-20 md:mb-4">
                      {/* Mobile: Simple pagination */}
                      <div className="md:hidden flex items-center justify-between gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ‹ ก่อนหน้า
                        </button>
                        <span className="text-sm text-gray-600">
                          หน้า {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ถัดไป ›
                        </button>
                      </div>
                      {/* Desktop: Full pagination */}
                      <div className="hidden md:block">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                          itemsPerPage={itemsPerPage}
                          totalItems={filteredProducts.length}
                          onItemsPerPageChange={(newItemsPerPage) => {
                            setItemsPerPage(newItemsPerPage);
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          ) : (
            <div className="text-center py-8 md:py-12 bg-white rounded-lg">
              <p className="text-gray-500 text-sm md:text-base">ไม่พบสินค้า</p>
            </div>
          )}
        </section>

        {/* ร้านค้ายอดนิยม */}
        {stores.length > 0 && (
          <section className="mb-4 md:mb-12">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">ร้านค้ายอดนิยม</h2>
              <Link
                to="/marketplace/stores"
                className="text-green-600 hover:text-green-700 font-semibold text-sm md:text-base"
              >
                {t('marketplace.viewAll')} →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4">
              {stores.map(store => (
                <StoreCard key={store.id} store={store} />
              ))}
            </div>
          </section>
        )}
      </div>
      
      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation />
    </div>
  );
};

export default MarketplaceHome;

